import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import seoConfig from './seoConfig.json'
import skTranslation from './translations/sk/index.json'

/* global document */

const toAbsoluteUrl = (value) =>
  value.startsWith('http') ? value : `${seoConfig.site.url}${value}`

const normalizePath = (pathname) => {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname
  return seoConfig.aliases[path] || path
}

const setMeta = (attribute, key, content) => {
  let element = document.head.querySelector(`meta[${attribute}="${key}"]`)

  if (!element) {
    element = document.createElement('meta')
    element.setAttribute(attribute, key)
    document.head.appendChild(element)
  }

  element.setAttribute('content', content)
}

const setCanonical = (href) => {
  let element = document.head.querySelector('link[rel="canonical"]')

  if (!element) {
    element = document.createElement('link')
    element.setAttribute('rel', 'canonical')
    document.head.appendChild(element)
  }

  element.setAttribute('href', href)
}

const buildStructuredData = (path, route) => {
  const { site } = seoConfig
  const pageUrl = `${site.url}${path === '/' ? '' : path}`
  const webPage = {
    '@type': route.pageType || 'WebPage',
    '@id': `${pageUrl}#webpage`,
    url: pageUrl,
    name: route.title,
    description: route.description,
    isPartOf: {
      '@id': `${site.url}/#website`,
    },
    about: {
      '@id': `${site.url}/#organization`,
    },
    inLanguage: 'sk-SK',
  }

  if (route.faq) {
    webPage.mainEntity = Array.from({ length: 10 }, (_, index) => ({
      '@type': 'Question',
      name: skTranslation.q[`q${index + 1}`],
      acceptedAnswer: {
        '@type': 'Answer',
        text: skTranslation.q[`a${index + 1}`],
      },
    }))
  }

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': ['LocalBusiness', 'SportsActivityLocation'],
        '@id': `${site.url}/#organization`,
        name: site.name,
        url: site.url,
        logo: toAbsoluteUrl(site.logo),
        image: toAbsoluteUrl(site.defaultImage),
        telephone: site.telephone,
        email: site.email,
        address: {
          '@type': 'PostalAddress',
          ...site.address,
        },
        geo: {
          '@type': 'GeoCoordinates',
          ...site.geo,
        },
        sameAs: site.sameAs,
      },
      {
        '@type': 'WebSite',
        '@id': `${site.url}/#website`,
        url: site.url,
        name: site.name,
        publisher: {
          '@id': `${site.url}/#organization`,
        },
        inLanguage: 'sk-SK',
      },
      webPage,
    ],
  }
}

const Seo = () => {
  const { pathname } = useLocation()

  useEffect(() => {
    const path = normalizePath(pathname)
    const route = seoConfig.routes[path]
    const isNotFound = !route
    const page = route || {
      title: 'Stránka nebola nájdená | BJJ Levice',
      description: 'Požadovaná stránka nebola nájdená.',
      noindex: true,
    }
    const canonicalUrl = `${seoConfig.site.url}${path === '/' ? '' : path}`
    const imageUrl = toAbsoluteUrl(page.image || seoConfig.site.defaultImage)

    document.title = page.title
    document.documentElement.lang = 'sk-SK'
    setCanonical(canonicalUrl)
    setMeta('name', 'description', page.description)
    setMeta('name', 'robots', page.noindex || isNotFound ? 'noindex, follow' : 'index, follow')
    setMeta('property', 'og:type', 'website')
    setMeta('property', 'og:locale', seoConfig.site.locale)
    setMeta('property', 'og:site_name', seoConfig.site.name)
    setMeta('property', 'og:title', page.title)
    setMeta('property', 'og:description', page.description)
    setMeta('property', 'og:url', canonicalUrl)
    setMeta('property', 'og:image', imageUrl)
    setMeta('property', 'og:image:alt', `${seoConfig.site.name} – ${page.title}`)
    setMeta('name', 'twitter:card', 'summary_large_image')
    setMeta('name', 'twitter:title', page.title)
    setMeta('name', 'twitter:description', page.description)
    setMeta('name', 'twitter:image', imageUrl)

    let structuredData = document.getElementById('seo-structured-data')
    if (!structuredData) {
      structuredData = document.createElement('script')
      structuredData.id = 'seo-structured-data'
      structuredData.type = 'application/ld+json'
      document.head.appendChild(structuredData)
    }
    structuredData.textContent = JSON.stringify(buildStructuredData(path, page))
  }, [pathname])

  return null
}

export default Seo
